import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Clock, User as UserIcon, Download, FileText } from 'lucide-react';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import getLoansList from '../api/GetLoansList';
import { setCurrentMember } from '../store/slices/membersSlice';

const DEFAULT_COVER = '/images/default_cover_w299.jpg';

export default function LateReturnsModal({ isOpen, onClose, onNavigate }) {
    const [lateLoans, setLateLoans] = useState([]);
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const delaiEmprunt = useSelector((state) => state.user.delaiEmprunt);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);

    useEffect(() => {
        const fetchLateLoans = async () => {
            if (!isOpen) return;

            try {
                let loans = await getLoansList(API_BASE, token, code_etablissement);

                // If student, only show personal late returns
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    loans = loans.filter(loan => loan.user_id == myId);
                }

                // Filter for active loans (not returned)
                const activeLoans = loans.filter(loan =>
                    loan.end_date.startsWith("0001")
                );

                // Calculate which loans are late
                const now = new Date();
                const late = activeLoans.filter(loan => {
                    const startDate = new Date(loan.start_date);
                    const prolongationDate = new Date(loan.prolongation);
                    if (prolongationDate > startDate) {
                        const dueDate = new Date(prolongationDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                        return now > dueDate;
                    }
                    const dueDate = new Date(startDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                    return now > dueDate;
                }).map(loan => {
                    const member = members.find(m => (m.ID || m.id) == loan.user_id);
                    const book = books.find(b => (b.item_id || b.id) == loan.item_id);

                    const startDate = new Date(loan.start_date);
                    const dueDate = new Date(startDate.getTime() + delaiEmprunt * 24 * 60 * 60 * 1000);
                    const daysLate = Math.floor((now - dueDate) / (24 * 60 * 60 * 1000));

                    return {
                        loan,
                        member,
                        book,
                        dueDate,
                        daysLate
                    };
                });

                setLateLoans(late);
            } catch (error) {
                console.error("Failed to fetch late loans:", error);
            }
        };

        fetchLateLoans();
    }, [isOpen, API_BASE, token, code_etablissement, delaiEmprunt, books, members, currentUser]);

    const handleMemberClick = (member) => {
        if (member) {
            dispatch(setCurrentMember(member));
            onClose();
            if (onNavigate) {
                onNavigate('Members');
            }
        }
    };

    const handleExportCSV = () => {
        if (lateLoans.length === 0) return;

        // Group by student
        const studentsMap = new Map();

        lateLoans.forEach(({ loan, member, book, daysLate }) => {
            const memberId = member?.ID || member?.id || 'Inconnu';
            if (!studentsMap.has(memberId)) {
                studentsMap.set(memberId, {
                    classe: member?.classe || 'Sans classe',
                    nom: member?.name || 'Inconnu',
                    livres: []
                });
            }

            const studentInfo = studentsMap.get(memberId);
            const titre = book?.title || 'Livre inconnu';
            const dateEmprunt = new Date(loan.start_date).toLocaleDateString();

            studentInfo.livres.push(`${titre} (emprunté le ${dateEmprunt}, ${daysLate} jours de retard)`);
        });

        // Convert Map to array and sort by class then name
        const exportData = Array.from(studentsMap.values())
            .sort((a, b) => {
                if (a.classe === b.classe) {
                    return a.nom.localeCompare(b.nom);
                }
                return a.classe.localeCompare(b.classe);
            })
            .map(student => ({
                'Classe': student.classe,
                'Élève': student.nom,
                'Livres en retard': student.livres.join(' | ')
            }));

        // Generate CSV
        const csv = Papa.unparse(exportData, {
            delimiter: ';' // Use semicolon for better Excel compatibility in Europe
        });

        // Add UTF-8 BOM explicitly using Uint8Array for perfect Excel compatibility
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `retards_bibliotheque_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPDF = () => {
        if (lateLoans.length === 0) return;

        const doc = new jsPDF();

        // Group by class then student
        const classMap = new Map();

        lateLoans.forEach(({ loan, member, book, daysLate }) => {
            const className = member?.classe || 'Sans classe';
            const memberId = member?.ID || member?.id || 'Inconnu';

            if (!classMap.has(className)) {
                classMap.set(className, new Map());
            }

            const studentsInClass = classMap.get(className);

            if (!studentsInClass.has(memberId)) {
                studentsInClass.set(memberId, {
                    nom: member?.name || 'Inconnu',
                    livres: []
                });
            }

            const studentInfo = studentsInClass.get(memberId);
            const titre = book?.title || 'Livre inconnu';
            const dateEmprunt = new Date(loan.start_date).toLocaleDateString();

            studentInfo.livres.push(`${titre}\n(emprunté le ${dateEmprunt}, ${daysLate}j de retard)`);
        });

        const sortedClasses = Array.from(classMap.keys()).sort();

        sortedClasses.forEach((className, index) => {
            if (index > 0) {
                doc.addPage();
            }

            doc.setFontSize(18);
            doc.setTextColor(40, 40, 40);
            doc.text(`Retours en retard - Classe : ${className}`, 14, 22);
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Généré le ${new Date().toLocaleDateString()}`, 14, 30);

            const studentsMap = classMap.get(className);
            const tableData = Array.from(studentsMap.values())
                .sort((a, b) => a.nom.localeCompare(b.nom))
                .map(student => [
                    student.nom,
                    student.livres.join('\n\n')
                ]);

            autoTable(doc, {
                startY: 35,
                head: [['Élève', 'Livres en retard']],
                body: tableData,
                theme: 'grid',
                styles: {
                    fontSize: 10,
                    cellPadding: 5,
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [59, 130, 246], // Blue-500 equivalent
                    textColor: 255,
                    fontStyle: 'bold'
                },
                columnStyles: {
                    0: { cellWidth: 60, fontStyle: 'bold' },
                    1: { cellWidth: 'auto' }
                },
                didDrawPage: function (data) {
                    // Footer with page number
                    const str = 'Page ' + doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                    doc.text(str, data.settings.margin.left, pageHeight - 10);
                }
            });
        });

        doc.save(`retards_bibliotheque_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm pointer-events-auto lg:pl-72">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
                >
                    <div className="p-6 border-b border-white/10 flex items-center justify-between bg-red-500/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-500/20">
                                <AlertCircle className="text-red-400" size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Retours en Retard</h3>
                                <p className="text-sm text-slate-400">{lateLoans.length} emprunt{lateLoans.length > 1 ? 's' : ''} en retard</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {lateLoans.length > 0 && currentUser?.role === 'Bibliothécaire' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleExportCSV}
                                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border border-white/10"
                                        title="Exporter en CSV (Excel)"
                                    >
                                        <Download size={16} />
                                        CSV
                                    </button>
                                    <button
                                        onClick={handleExportPDF}
                                        className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border border-white/10"
                                        title="Télécharger en PDF (Trié par classe)"
                                    >
                                        <FileText size={16} />
                                        PDF
                                    </button>
                                </div>
                            )}
                            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                                <X size={24} />
                            </button>
                        </div>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        {lateLoans.length > 0 ? (
                            <div className="space-y-4">
                                {lateLoans.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="flex gap-4 bg-slate-800/30 p-4 rounded-xl border border-red-500/20 hover:border-red-500/40 transition-colors cursor-pointer"
                                        onClick={() => handleMemberClick(item.member)}
                                    >
                                        <img
                                            src={item.book?.cover || DEFAULT_COVER}
                                            alt={item.book?.title || "Livre"}
                                            className="w-16 h-24 object-cover rounded-md"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = DEFAULT_COVER;
                                            }}
                                        />
                                        <div className="flex-1">
                                            <h4 className="font-bold text-white text-lg mb-1">{item.book?.title || "Livre inconnu"}</h4>
                                            <p className="text-slate-400 text-sm mb-2">{item.book?.author || "Auteur inconnu"}</p>

                                            <div className="flex items-center gap-2 mb-2">
                                                <UserIcon size={14} className="text-slate-500" />
                                                <span className="text-sm text-slate-300">{item.member?.name || "Membre inconnu"}</span>
                                            </div>

                                            <div className="flex items-center gap-4 text-xs">
                                                <div className="flex items-center gap-1 text-slate-400">
                                                    <Clock size={12} />
                                                    <span>Retour prévu: {item.dueDate.toLocaleDateString()}</span>
                                                </div>
                                                <div className="px-2 py-1 bg-red-500/20 text-red-400 rounded-md font-medium">
                                                    {item.daysLate} jour{item.daysLate > 1 ? 's' : ''} de retard
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <AlertCircle className="mx-auto text-slate-600 mb-4" size={48} />
                                <p className="text-slate-400">Aucun retour en retard</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
